/**
 * SCWT-PM-V2 · Phase 8 · Feb 2026 — PWS Partner Marketing Resources
 * self-view. Only shows partner-audience assets. Only the caller's
 * own personalised render is ever served.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, FileText, Download, Eye, AlertTriangle } from "lucide-react";
import api from "@/lib/api";

export default function PwsPartnerMarketingResources() {
  const [loading, setLoading] = useState(true);
  const [catalog, setCatalog] = useState(null);
  const [previewSlug, setPreviewSlug] = useState(null);
  const [previewHtml, setPreviewHtml] = useState("");
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/partner/pm-marketing-resources/catalog");
        setCatalog(data);
      } catch (e) {
        setErr(e.response?.data?.detail || "Could not load resources");
      } finally { setLoading(false); }
    })();
  }, []);

  const preview = async (slug) => {
    setPreviewSlug(slug); setPreviewHtml("");
    try {
      const r = await api.get(`/partner/pm-marketing-resources/${slug}/preview.html`, { responseType: "text" });
      setPreviewHtml(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Preview failed");
    }
  };

  const download = (slug) => {
    // Server sets Content-Disposition — hit the endpoint via a link
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/partner/pm-marketing-resources/${slug}/download.html`;
    window.open(url, "_blank", "noopener");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <Link to="/partner" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-cyan-700">
            <ArrowLeft className="w-3 h-3" /> Back to partner dashboard
          </Link>
          <h1 data-testid="pws-marketing-h1" className="mt-1 text-xl font-bold text-slate-900 flex items-center gap-2">
            <FileText className="w-5 h-5 text-cyan-600" />
            Marketing resources
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Partner-branded resources for introducing the AWARE Property
            Manager program. Nothing is sent automatically.
          </p>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-6 py-6">
        {err && (
          <div className="mb-4 rounded-md border border-red-300 bg-red-50 px-4 py-2 text-sm text-red-800 inline-flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />{err}
          </div>
        )}
        {loading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {(catalog?.assets || []).map((a) => (
              <div key={a.slug}
                   data-testid={`pws-marketing-card-${a.slug}`}
                   className="rounded-lg border border-slate-200 bg-white p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-sm font-semibold text-slate-800">{a.label}</div>
                    <div className="text-[11px] text-slate-500 mt-1">{a.slug}</div>
                  </div>
                  {!a.available && (
                    <span className="text-[10px] px-2 py-0.5 rounded bg-amber-100 text-amber-800">
                      Temporarily unavailable
                    </span>
                  )}
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <button
                    disabled={!a.available}
                    data-testid={`pws-marketing-preview-${a.slug}`}
                    onClick={() => preview(a.slug)}
                    className="inline-flex items-center gap-1 text-[12px] px-2.5 py-1.5 rounded bg-slate-100 hover:bg-slate-200 disabled:opacity-40"
                  >
                    <Eye className="w-3 h-3" /> Preview
                  </button>
                  <button
                    disabled={!a.available}
                    data-testid={`pws-marketing-download-${a.slug}`}
                    onClick={() => download(a.slug)}
                    className="inline-flex items-center gap-1 text-[12px] px-2.5 py-1.5 rounded bg-cyan-600 text-white hover:bg-cyan-700 disabled:opacity-40"
                  >
                    <Download className="w-3 h-3" /> Download
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        {previewSlug && previewHtml && (
          <div className="mt-6 rounded-lg border border-slate-200 bg-white p-4">
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">
              Preview — {previewSlug}
            </div>
            <iframe title="preview"
                    data-testid="pws-marketing-preview-iframe"
                    className="w-full min-h-[520px] border border-slate-100 rounded"
                    srcDoc={previewHtml} />
          </div>
        )}
      </main>
    </div>
  );
}
